import TreeList from "./TreeList.jsx";
import { useQuery } from "react-query";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Weather } from "@modules/Weather";
import { ViewTypeButton } from "../Button";
// import { EmptyProject } from "./EmptyProject";
import SensorList from "./SensorList.jsx";
import { useTranslation } from "react-i18next";
import Spinner from "@components/Spinner/Spinner";
import "@modules/Projects/components/Project.css";
import { Breadcrumb } from "@components/Breadcrumb";
import AddButton from "@components/Button/AddButton";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import ServerError from "@components/Error/ServerError";
import PageNotFound from "@components/Error/PageNotFound";
import SensorDetail from "@modules/Sensors/components/SensorDetail";
import TreeDetail from "@modules/Trees/components/TreeDetail";
import { Col, Container, Row, Tab, Tabs } from "react-bootstrap";
import { getProject } from "@modules/Projects/services/projectCrud";
import TreeListMobileView from "@modules/Trees/components/TreeListMobileView";
import SensorMobileListView from "@modules/Sensors/components/SensorMobileListView";
import IrrigationAreaList from "@modules/IrrigationArea/components/IrrigationAreaList";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
import IrrigationAreaDetail from "@modules/IrrigationArea/components/IrrigationAreaDetail";
import IrrigationAreaForm from "@modules/IrrigationArea/components/Form/IrrigationAreaForm";
import AddAllForm from "@modules/Projects/components/Form/AddAllForm";
import TreeForm from "@modules/Trees/components/Form/AddForm";
import useMatchedElementHeight from "@hooks/useMatchedElementHeight";

export default function ProjectDetailListView({ project, setProject }) {
  const { pid, listType } = useParams();
  const navigate = useNavigate();
  const [formValue, setFormValue] = useState();
  const [isUpdate, setIsUpdate] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [isTreeUpdate, setIsTreeUpdate] = useState(false);
  const [openTreeForm, setOpenTreeForm] = useState(false);
  const [openImageForm, setOpenImageForm] = useState(false);
  const [openAllForm, setOpenAllForm] = useState(false);
  const [areas, setAreas] = useState();
  const [trees, setTrees] = useState();
  const [sensors, setSensors] = useState();
  const [detail, setDetail] = useState();
  const { t } = useTranslation();
  const { mobile } = useSelector((state) => state.mobile);
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("selectedArea");
  const tid = query.get("selectedTree");
  const sid = query.get("selectedSensor");
  const [aid, setAid] = useState();
  const treeListColumnRef = React.useRef(null);
  const sensorListColumnRef = React.useRef(null);
  const treeDetailHeight = useMatchedElementHeight(
    treeListColumnRef,
    ".table-wrapper",
    !mobile && tid
  );
  const sensorDetailHeight = useMatchedElementHeight(
    sensorListColumnRef,
    ".table-wrapper",
    !mobile && sid
  );
  // const isProjectLoaded = project != null;

  //fetch single project
  // const {
  //   isLoading,
  //   data: projectFetched,
  //   isError,
  //   error,
  // } = useQuery(["project", pid], () => getProject(pid), {
  //   refetchOnWindowFocus: false,
  //   refetchOnMount: false,
  //   // enabled: !isProjectLoaded,
  //   onSuccess: (data) => {
  //     const { areas } = data;
  //     let trees = areas
  //       .map((area) =>
  //         area.tree_set.map((tree) => {
  //           tree["area"] = area.name;
  //           tree["areaId"] = area.id;
  //           return tree;
  //         })
  //       )
  //       .flat();
  //     setTrees(trees);
  //     setAreas(areas);
  //     const areasensors = areas
  //       .map((area) =>
  //         area.tree_set.map((tree) =>
  //           tree.installations.map((installation) => {
  //             const sensor = installation.sensor;
  //             sensor["tree"] = tree.name;
  //             return sensor;
  //           })
  //         )
  //       )
  //       .flat();
  //     setSensors(areasensors.flat());
  //   },
  // });

  const shouldFetchProject = pid !== "without-project" && (!project || project?.id !== +pid);
  const scheduledGetProject = createScheduledQueryFn(
    () => getProject(pid),
    REQUEST_PRIORITIES.HIGH,
    "getProject"
  );
  const {
    isLoading,
    data: projectFetched,
    isError,
    error,
  } = useQuery(["project", pid], scheduledGetProject, {
    enabled: shouldFetchProject,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onSuccess: (data) => {
      setProject?.(data); // if passed via props
    },
  });

  const effectiveProject = shouldFetchProject ? projectFetched : project;

  // Determine active tab from URL parameter
  const validTabs = ["areas", "trees", "sensors"];
  const activeTab = validTabs.includes(listType) ? listType : "trees";

  // Update URL when tab changes
  const handleTabSelect = (key) => {
    const query = new URLSearchParams(location.search);

    // Remove selection parameters that don't belong to the new tab
    if (key === "areas") {
      query.delete("selectedTree");
      query.delete("selectedSensor");
    } else if (key === "trees") {
      query.delete("selectedArea");
      query.delete("selectedSensor");
    } else if (key === "sensors") {
      query.delete("selectedArea");
      query.delete("selectedTree");
    }

    navigate(`/project/${pid}/list/${key}${query.toString() ? `?${query.toString()}` : ""}`, {
      replace: true,
    });
  };

  // Redirect to default tab if no valid tab is in URL
  useEffect(() => {
    if (!listType || !validTabs.includes(listType)) {
      const query = new URLSearchParams(location.search);
      navigate(`/project/${pid}/list/trees${query.toString() ? `?${query.toString()}` : ""}`, {
        replace: true,
      });
    }
  }, [listType, pid, location.search, navigate]);

  useEffect(() => {
    if (!effectiveProject) return;

    // Get all tree IDs from areas
    const treeIdsInAreas = new Set();
    const treesFromAreas = (effectiveProject.areas || []).flatMap((area) =>
      (area.tree_set || []).map((tree) => {
        treeIdsInAreas.add(tree.id);
        return {
          ...tree,
          area: area.name,
          areaId: area.id,
        };
      })
    );

    // Get trees without area (trees in project but not in any area)
    const treesWithoutArea = (effectiveProject.trees || [])
      .filter((tree) => !treeIdsInAreas.has(tree.id))
      .map((tree) => ({
        ...tree,
        area: t("without_location"),
        areaId: null,
      }));

    // Combine both
    const allTrees = [...treesFromAreas, ...treesWithoutArea];
    setTrees(allTrees);

    setAreas(effectiveProject.areas || []);

    const sensors = (effectiveProject.areas || []).flatMap((area) =>
      (area.tree_set || []).flatMap((tree) =>
        (tree.installations || []).map((installation) => ({
          ...installation.sensor,
          tree: tree.name,
          area: area.name,
          areaId: area.id,
        }))
      )
    );
    setSensors(sensors);
  }, [effectiveProject, t]);

  if (isLoading) return <Spinner height={"70vh"} />;
  if (isError && error.response && error.response.status === 404) return <PageNotFound />;
  if (isError) return <ServerError />;

  const breadcrumbTitle = (
    <span className="d-inline-flex align-items-center gap-3 flex-wrap">
      <span>{project?.name}</span>
      <Weather weather={project?.weather} iconWidth={"1.2rem"} hideLabel />
    </span>
  );

  return (
    <>
      <Breadcrumb
        title={breadcrumbTitle}
        addButton={<AddButton label={"button.add_all"} action={() => setOpenAllForm(true)} />}
        viewType={<ViewTypeButton id={pid} />}
      />

      <Container className="project-container entity-list-page">
        {/* {mobile ? ( */}
        {/*   <> */}
        {/*     {areas?.length === 0 ? ( */}
        {/*       <>{<EmptyProject width={150} />}</> */}
        {/*     ) : ( */}
        {/*       <TreeListMobileView trees={trees} /> */}
        {/*     )} */}
        {/*   </> */}
        {/* ) : ( */}
        <>
          <>
            <Tabs
              data-testid="tabs"
              activeKey={activeTab}
              onSelect={handleTabSelect}
              id="uncontrolled-tab-example"
              className="project-detail-tabs mb-3"
            >
              <Tab eventKey="areas" title={t("areas")}>
                <Row className="entity-split-row g-3">
                  <Col lg={id ? 6 : 12} className="entity-master-column">
                    {areas && (
                      <IrrigationAreaList
                        areas={areas}
                        setIsUpdate={setIsUpdate}
                        setDetail={setDetail}
                        setOpenForm={setOpenForm}
                      />
                    )}
                  </Col>
                  {id && (
                    <Col lg={6} className="entity-detail-column">
                      <div className="entity-detail-height-shell">
                        <IrrigationAreaDetail />
                      </div>
                    </Col>
                  )}
                </Row>
              </Tab>
              <Tab eventKey="trees" title={t("trees")}>
                {mobile ? (
                  trees && <TreeListMobileView trees={trees} />
                ) : (
                  <Row className="entity-split-row g-3">
                    <Col ref={treeListColumnRef} lg={tid ? 6 : 12} className="entity-master-column">
                      {trees && (
                        <TreeList
                          trees={trees}
                          setAid={setAid}
                          setFormValue={setFormValue}
                          setOpenImageForm={setOpenImageForm}
                          openImageForm={openImageForm}
                          project={project}
                          setIsUpdate={setIsTreeUpdate}
                          setOpenForm={setOpenTreeForm}
                          formValue={formValue}
                        />
                      )}
                    </Col>
                    {tid && (
                      <Col lg={6} className="tree-detail-column entity-detail-column">
                        <div
                          className="entity-detail-height-shell"
                          style={treeDetailHeight ? { height: `${treeDetailHeight}px` } : undefined}
                        >
                          <TreeDetail pid={pid} aid={aid} trees={trees} />
                        </div>
                      </Col>
                    )}
                  </Row>
                )}
              </Tab>
              <Tab eventKey="sensors" title={t("sensors")}>
                {mobile ? (
                  sensors && <SensorMobileListView sensors={sensors} />
                ) : (
                  <Row className="entity-split-row g-3">
                    <Col
                      ref={sensorListColumnRef}
                      lg={sid ? 6 : 12}
                      className="entity-master-column"
                    >
                      {sensors && <SensorList sensors={sensors} />}
                    </Col>
                    {sid && (
                      <Col lg={6} className="sensor-detail-column entity-detail-column">
                        <div
                          className="entity-detail-height-shell"
                          style={
                            sensorDetailHeight ? { height: `${sensorDetailHeight}px` } : undefined
                          }
                        >
                          <SensorDetail />
                        </div>
                      </Col>
                    )}
                  </Row>
                )}
              </Tab>
            </Tabs>
          </>
        </>
        {/* )} */}
      </Container>
      <IrrigationAreaForm
        isUpdate={isUpdate}
        detail={detail}
        setIsUpdate={setIsUpdate}
        show={openForm}
        setOpenForm={setOpenForm}
        selectedArea={detail}
        pid={pid}
        setAreaValues={(area) => {
          // Update the areas array with the modified area
          setAreas((prevAreas) => prevAreas.map((a) => (a.id === area.id ? area : a)));
          // Update project areas with the modified area
          setProject((prevProject) => {
            if (!prevProject) return prevProject;
            return {
              ...prevProject,
              areas:
                prevProject.areas?.map((a) => (a.id === area.id ? { ...a, ...area } : a)) || [],
            };
          });
        }}
      />
      <AddAllForm
        isUpdate={isUpdate}
        detail={detail}
        setIsUpdate={setIsUpdate}
        show={openAllForm}
        setOpenForm={setOpenAllForm}
        pid={pid}
      />
      <TreeForm
        isUpdate={isTreeUpdate}
        formValue={formValue}
        setIsUpdate={setIsTreeUpdate}
        show={openTreeForm}
        setOpenForm={setOpenTreeForm}
        setTreeValues={(tree) => {
          // Update the trees array with the modified tree (preserve area and areaId)
          setTrees((prevTrees) =>
            prevTrees.map((t) =>
              t.id === tree.id ? { ...tree, area: t.area, areaId: t.areaId } : t
            )
          );
          // Update areas array with the modified tree in tree_set
          setAreas((prevAreas) =>
            prevAreas.map((area) => ({
              ...area,
              tree_set: area.tree_set?.map((t) => (t.id === tree.id ? { ...t, ...tree } : t)) || [],
            }))
          );
          // Update project areas tree_set with the modified tree
          setProject((prevProject) => {
            if (!prevProject) return prevProject;
            return {
              ...prevProject,
              areas:
                prevProject.areas?.map((area) => ({
                  ...area,
                  tree_set:
                    area.tree_set?.map((t) => (t.id === tree.id ? { ...t, ...tree } : t)) || [],
                })) || [],
            };
          });
        }}
      />
    </>
  );
}
